// sections/HeroSection.tsx
import React, { useState } from "react";
import { Row, Col, Button, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import { BicepsFlexed, ChevronLeft, ChevronRight, Rocket, TrendingUp, ExternalLink } from 'lucide-react';
import heroImage from "./images/photo-1503387762-592deb58ef4e.avif";
import pitchImage from "./images/bidbro-pitch-1.jpg";
import './HeroSection.css';

interface HeroSectionProps {
}

interface VideoItem {
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
}

interface SlideItem {
  id: string;
  type: 'video' | 'investment';
  title: string;
  description: string;
  url?: string;
  image?: string;
  content?: React.ReactNode;
}

const HeroSection: React.FC<HeroSectionProps> = ({ }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Combined slides data - investment slide first, then videos
  const slides: SlideItem[] = [
    {
      id: "investment",
      type: "investment",
      title: "Invest in BidBro",
      description: "Join our growth journey to scale massively",
      image: pitchImage,
      content: (
        <div className="bg-white rounded p-4 shadow mb-4">
          <div className="position-relative">
            <img
              src={pitchImage}
              alt="BidBro Investment Opportunity"
              className="img-fluid w-100 rounded mb-4"
            />
            <div className="investment-content">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-success rounded-circle p-2 me-3" style={{ width: '40px', height: '40px' }}>
                  <TrendingUp className="text-white" size={20} />
                </div>
                <h4 className="mb-0 fw-bold text-primary">Investment Opportunity</h4>
              </div>
              
              <p className="text-dark mb-4" style={{ lineHeight: '1.6', fontSize: '1.1rem' }}>
                With over a trillion dollars spent annually on home improvement, BidBro has the opportunity to expand far beyond its current stage. Our aspiration is to evolve from a company valued in the tens of millions into one operating on a multi-billion-dollar level. While results are never assured, this demonstrates the scale of what we're pursuing.
              </p>
              
              <div className="d-flex flex-column flex-sm-row gap-3 mb-3">
                <a
                  href="https://wefunder.com/bidbro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-lg d-flex align-items-center justify-content-center"
                >
                  <TrendingUp size={20} className="me-2" />
                  View Our Wefunder Campaign
                  <ExternalLink size={16} className="ms-2" />
                </a>
                <a
                  href="https://help.wefunder.com/testing-the-waters-legal-disclosure"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                >
                  Legal Disclosure
                  <ExternalLink size={16} className="ms-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "1",
      type: "video",
      title: "🏠 BidBro 💪",
      description: "Save weeks of time & 20% or more on your home improvement project!",
      url: "https://youtube.com/shorts/ToEwqoZqlQA"
    },
    {
      id: "2",
      type: "video",
      title: "BidBro: 🏠 YOUR COMPLETE HOME PROJECT SOLUTION! 🏠",
      description: "BidBro doesn't just connect you with contractors - we help manage your ENTIRE project from start to finish! ",
      url: "https://youtube.com/shorts/SdmLIh0xHLo"
    }

    // Add more slides here as needed
  ];

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlide = slides[currentSlideIndex];

  // Extract YouTube video ID for embedding
  const getYouTubeVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const renderSlideContent = () => {
    if (currentSlide.type === 'investment') {
      return currentSlide.content;
    } else if (currentSlide.type === 'video') {
      const videoId = getYouTubeVideoId(currentSlide.url!);
      return (
        <div className="bg-white rounded p-4 shadow mb-4">
          {videoId && (
            <div className="ratio ratio-16x9">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                title={currentSlide.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded"
              ></iframe>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <section
      className="hero-section position-relative text-white d-flex align-items-center my-3 my-md-5 my-lg-5"
      style={{
        backgroundImage: `url(${heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="overlay position-absolute"></div>
      <div className="container position-relative">
        <div className="max-w-5xl">
          <h1 className="display-4 fw-bold mb-4 text-light">
            Save Time & Money Hiring Contractors
          </h1>
          <p className="fs-4 mb-5">
            Post your job, get multiple quotes up-front, skip the meetings, and save up to 20% or more!
          </p>

          {/* Slide Carousel */}
          <div>
            {/* Slide Navigation - only show if more than 1 slide */}
            {slides.length > 1 && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  color="light"
                  size="sm"
                  onClick={prevSlide}
                  disabled={slides.length <= 1}
                >
                  <ChevronLeft size={16} className="me-1" /> Previous
                </Button>
                <span className="text-white small bg-dark bg-opacity-75 px-3 py-1 rounded">
                  {currentSlide.type === 'investment' ? 'Investment Opportunity' : `Video ${currentSlideIndex} of ${slides.length - 1}`} ({currentSlideIndex + 1} of {slides.length})
                </span>
                <Button
                  color="light"
                  size="sm"
                  onClick={nextSlide}
                  disabled={slides.length <= 1}
                >
                  Next <ChevronRight size={16} className="ms-1" />
                </Button>
              </div>
            )}

            {/* Slide Content */}
            {renderSlideContent()}
          </div>

         {/* Release 2.1.0 Highlight */}
         <div className="bg-gradient-primary rounded p-3 shadow-sm latest-release-margins">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div className="bg-warning rounded-circle p-2 me-3" style={{ width: '40px', height: '40px' }}>
                    <BicepsFlexed className="text-dark" size={20} />
                  </div>
                  <div>
                    <h5 className="mb-1 text-white fw-bold" >BidBro Release 2.1.0 is here</h5>
                    <h6 className="mb-1 text-white fw-bold" >Launched <Rocket className="ms-1 me-1" />08/11/2025!</h6>
                    <div className="mb-0 text-white-75">
                      <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                        <li>Added construction projects for 46 major US cities including New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, San Francisco, and 37 more major metropolitan areas</li>
                        <li>Now covering 24 states coast-to-coast with over <strong>27,000 public construction projects</strong></li>
                        <li>Significantly expanded opportunities for contractors and businesses nationwide</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-center text-md-end mt-2 mt-md-0">
                <Link
                  to="/jobs"
                  className="px-1 px-sm-2 px-md-3 py-1 py-md-2 me-2 btn btn-warning rounded text-nowrap"
                  style={{ fontSize: '0.8rem' }}
                >
                  <BicepsFlexed size={16} className="me-1" />
                  <span className="d-none d-sm-inline">Browse Projects</span>
                  <span className="d-sm-none">Projects</span>
                </Link>
                <Link
                  id="btnReleases"
                  to="/releases"
                  className="px-1 px-sm-2 px-md-3 py-1 py-md-2 btn btn-secondary rounded text-nowrap"
                  style={{ fontSize: '0.8rem' }}
                >
                  <BicepsFlexed size={16} className="me-1" />
                  <span className="d-none d-sm-inline">View Release Notes</span>
                  <span className="d-sm-none">Release Notes</span>
                </Link>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;